import ScratchJr from '../../../ScratchJr';
import Thumbs from '../../../ui/Thumbs';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import CompletionModal from '../../shared/CompletionModal';
import {gn} from '../../../../utils/lib';
import {evaluateSuccessCriteria} from '../../../../../../web/ai/activity-success';

const DEFAULT_PHASE = 'TASK_1_BLUUX';
const PHASES = Object.freeze({
    TASK_1_BLUUX: 'TASK_1_BLUUX',
    CHAR_SWITCH: 'CHAR_SWITCH',
    TASK_2_DOG: 'TASK_2_DOG',
    BONUS: 'BONUS',
    COMPLETE: 'COMPLETE'
});

const MEMBERS = ['bluux', 'dog'];

export default class HelloFriendFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._stage = container.querySelector('.stage') || container;
        this._paletteRestrictor = null;
        this._phase = DEFAULT_PHASE;
        this._config = this._buildConfig(activityData);
        this._idleTimers = [];
        this._phaseTimeout = null;
        this._lastInteractionAt = Date.now();
        this._sessionStartedAt = Date.now();
        this._idleTimeMs = 0;
        this._idlePausedAt = null;
        this._selectedRole = null;
        this._hintEvents = [];
        this._voiceHistory = [];
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._speechVoice = null;
        this._speechVoiceScore = 0;
        this._speechQueue = Promise.resolve();
        this._speechRunId = 0;
        this._interactionSettleTimer = null;
        this._destroyed = false;
        this._doneBtn = null;
        this._flagTapCount = 0;
        this._characterSwitches = 0;
        this._milestones = {
            bluuxFlag: false,
            bluuxSay: false,
            dogFlag: false,
            dogSay: false
        };
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);
        this._onPointerDown = this._handleMeaningfulInteraction.bind(this);
        this._onVisibilityChange = this._stopNarration.bind(this);
        this._onPageHide = this._stopNarration.bind(this);
    }

    async init () {
        this._paletteRestrictor = new PaletteRestrictor(this._container, []);
        this._attachListeners();
        this._initVoiceSelection();
        await this._waitForPreferredVoice();

        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        if (saved && Array.isArray(saved.hintEvents)) {
            this._hintEvents = saved.hintEvents.slice();
        }
        if (saved && typeof saved.flagTapCount === 'number') {
            this._flagTapCount = saved.flagTapCount;
        }
        if (saved && typeof saved.characterSwitches === 'number') {
            this._characterSwitches = saved.characterSwitches;
        }
        if (saved && saved.milestones) {
            this._milestones = {...this._milestones, ...saved.milestones};
        }

        const savedPhase = saved && saved.currentState && PHASES[saved.currentState]
            ? saved.currentState
            : null;
        if (savedPhase) {
            const resumePhase = savedPhase === PHASES.COMPLETE ? PHASES.BONUS : savedPhase;
            this._setPhase(resumePhase, {silent: true});
            this._ensureExpectedSelection();
            this._syncVisualState();
            this._startIdleScaffolding();
            this._queueNarration('VO-INTRO', this._buildResumePrompt(resumePhase), {
                speaker: this._speakerForPhase(resumePhase),
                clear: true
            });
            await this._persistProgress();
            return;
        }

        this._setPhase(PHASES.TASK_1_BLUUX, {silent: true});
        this._selectRole('bluux');
        this._syncVisualState();
        // Re-select after the editor's page render completes (it defaults to first sprite)
        setTimeout(() => this._selectRole('bluux'), 100);
        this._phaseTimeout = setTimeout(() => {
            if (!this._destroyed) {
                this._queueNarration('VO-INTRO', null, {speaker: 'bluux', clear: true})
                    .then(() => {
                        if (!this._destroyed && this._phase === PHASES.TASK_1_BLUUX) {
                            return new Promise(resolve => setTimeout(resolve, 2000));
                        }
                    })
                    .then(() => {
                        if (!this._destroyed && this._phase === PHASES.TASK_1_BLUUX) {
                            this._queueNarration('VO-FIRST-SAY', null, {speaker: 'bluux'});
                        }
                    });
            }
        }, this._config.introDelayMs);
        await this._persistProgress();
    }

    onStateChange (prevState, newState) {
        if (prevState === 'SUCCESS' && newState === 'BUILDING') {
            this._resetForReplay();
            return;
        }

        if (newState === 'RUNNING') {
            this._flagTapCount++;
            this._clearIdleTimers();
            return;
        }

        if (newState === 'VALIDATING') {
            this._validateRun();
            return;
        }

        if (newState === 'BUILDING' || newState === 'READY') {
            this._syncVisualState();
            this._startIdleScaffolding();
        }
    }

    getBlockCount () {
        return ScratchJr.collectActivityBlocks().length;
    }

    evaluate () {
        const simState = this._buildSimState();
        const shared = evaluateSuccessCriteria(this._activityData, simState);
        return Object.assign({}, shared, {
            usedBlocks: this.getBlockCount(),
            storyPhase: this._phase,
            flagTapCount: this._flagTapCount
        });
    }

    _buildSimState () {
        return {
            scripts: {
                bluux: this._getSpriteBlocks('bluux'),
                dog: this._getSpriteBlocks('dog')
            },
            phaseFlags: {
                completed: this._phase === PHASES.COMPLETE,
                storyPhase: this._phase,
                flagTapCount: this._flagTapCount
            }
        };
    }

    showHint () {
        // No-op — idle scaffolding handles hints automatically
    }

    captureRunStartPosition () {
        // ScratchJr handles sprite reset for each green-flag run.
    }

    shouldResetSpritesBeforeRun () {
        return true;
    }

    shouldEvaluateWhileThreadsRunning () {
        return false;
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }
        this._resetForReplay();
        return true;
    }

    destroy () {
        this._destroyed = true;
        this._clearIdleTimers();
        if (this._interactionSettleTimer) {
            clearTimeout(this._interactionSettleTimer);
            this._interactionSettleTimer = null;
        }
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._stopNarration();
        if (window.speechSynthesis && typeof window.speechSynthesis.removeEventListener === 'function') {
            window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangeHandler);
        }
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        window.removeEventListener('pagehide', this._onPageHide);
        window.removeEventListener('beforeunload', this._onPageHide);
        document.removeEventListener('pointerdown', this._onPointerDown, true);
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
            this._paletteRestrictor = null;
        }
    }

    // ──────────────────────── Internal helpers ────────────────────────

    _buildConfig (activityData) {
        const helloFriend = activityData.helloFriend || {};
        const firstPage = activityData.projectTemplate && activityData.projectTemplate.pages
            ? activityData.projectTemplate.pages[0]
            : {};
        const sprites = Array.isArray(firstPage.sprites) ? firstPage.sprites : [];
        const byName = sprites.reduce((acc, sprite) => {
            acc[String(sprite && sprite.name || '').toLowerCase()] = sprite;
            return acc;
        }, {});

        const bluux = byName.bluux || byName.penguin || {name: 'Bluux', x: 60, y: 188};
        const dog = byName.dog || {name: 'Dog', x: 380, y: 188};

        return {
            introDelayMs: helloFriend.introDelayMs || 4200,
            restartPhase: helloFriend.restartPhase || PHASES.TASK_1_BLUUX,
            availableBlocks: helloFriend.availableBlocks || {},
            idleScaffolding: helloFriend.idleScaffolding || {},
            voiceOver: helloFriend.voiceOver || {},
            members: {
                bluux: {
                    role: 'bluux',
                    spriteName: bluux.name || 'Bluux',
                    speaker: 'bluux',
                    homeX: bluux.x || 60,
                    homeY: bluux.y || 188
                },
                dog: {
                    role: 'dog',
                    spriteName: dog.name || 'Dog',
                    speaker: 'dog',
                    homeX: dog.x || 380,
                    homeY: dog.y || 188
                }
            },
            voiceConfidenceThreshold: 90
        };
    }

    _attachListeners () {
        document.addEventListener('pointerdown', this._onPointerDown, true);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        window.addEventListener('pagehide', this._onPageHide);
        window.addEventListener('beforeunload', this._onPageHide);
    }

    _handleMeaningfulInteraction () {
        if (this._idlePausedAt) {
            this._idleTimeMs += Math.max(0, Date.now() - this._idlePausedAt);
            this._idlePausedAt = null;
        }

        this._lastInteractionAt = Date.now();
        this._clearIdleTimers();
        this._startIdleScaffolding();
        if (this._interactionSettleTimer) {
            clearTimeout(this._interactionSettleTimer);
        }
        this._interactionSettleTimer = setTimeout(() => {
            this._interactionSettleTimer = null;
            this._settleInteraction();
        }, 80);
    }

    _settleInteraction () {
        const selectedRole = this._getSelectedRole();
        if (selectedRole && selectedRole !== this._selectedRole) {
            this._selectedRole = selectedRole;
            this._characterSwitches++;
            ActivityAudio.play('sparkle');
            if (this._phase === PHASES.CHAR_SWITCH && selectedRole === 'dog') {
                this._enterTask2();
                return;
            }
        }

        this._checkPhaseProgress();
    }

    _checkPhaseProgress () {
        const bluuxHasFlag = this._roleHasAnyBlock('bluux', ['onflag']);
        const bluuxHasSay = this._roleHasAnyBlock('bluux', ['say']);
        const dogHasFlag = this._roleHasAnyBlock('dog', ['onflag']);
        const dogHasSay = this._roleHasAnyBlock('dog', ['say']);

        if (!this._milestones.bluuxFlag && bluuxHasFlag) {
            this._milestones.bluuxFlag = true;
            this._persistProgress();
        }
        if (!this._milestones.bluuxSay && bluuxHasSay) {
            this._milestones.bluuxSay = true;
            this._persistProgress();
            if (this._phase === PHASES.TASK_1_BLUUX) {
                this._queueNarration('VO-END-HINT', null, {speaker: 'bluux'});
            }
        }

        const bluuxHasEnd = this._roleHasAnyBlock('bluux', ['endstack']);
        if (this._phase === PHASES.TASK_1_BLUUX && bluuxHasFlag && bluuxHasSay && bluuxHasEnd) {
            this._setPhase(PHASES.CHAR_SWITCH);
            this._queueNarration('VO-DOG-SAYS', null, {speaker: 'bluux', clear: true});
            return;
        }

        if (!this._milestones.dogFlag && dogHasFlag) {
            this._milestones.dogFlag = true;
            this._persistProgress();
        }
        if (!this._milestones.dogSay && dogHasSay) {
            this._milestones.dogSay = true;
            this._persistProgress();
            if (this._phase === PHASES.TASK_2_DOG) {
                this._queueNarration('VO-END-HINT', null, {speaker: 'bluux'});
            }
        }

        const dogHasEnd = this._roleHasAnyBlock('dog', ['endstack']);
        if (this._phase === PHASES.TASK_2_DOG && dogHasFlag && dogHasSay && this._roleHasAnyBlock('dog', ['wait']) && dogHasEnd) {
            this._enterBonus();
            return;
        }
    }

    _validateRun () {
        this._checkPhaseProgress();
        this._persistProgress();
        this._stateMachine.transition('BUILDING');
    }

    _enterTask2 () {
        this._setPhase(PHASES.TASK_2_DOG);
        this._selectRole('dog');
        this._queueNarration('VO-DOG-SAYS', null, {speaker: 'dog', clear: true});
        this._startIdleScaffolding();
    }

    _enterBonus () {
        this._setPhase(PHASES.BONUS);
        this._startIdleScaffolding();
        this._showDoneButton();
        this._queueNarration('VO-RUN-IT', null, {speaker: 'bluux', clear: true});
    }

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }
        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-hello-friend-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            this._enterComplete();
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    _enterComplete () {
        this._setPhase(PHASES.COMPLETE);
        this._hideDoneButton();
        ActivityAudio.play('victory');
        this._queueNarration('VO-COMPLETE', null, {speaker: 'system', clear: true});
        this._persistProgress({status: 'completed', completedAt: new Date().toISOString()});
        CompletionModal.show({
            title: 'Hello, Friend!',
            message: 'You made two friends have a conversation! Great job!',
            onReplay: () => this.restartActivity()
        });
    }

    _setPhase (phase, opts = {}) {
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._phase = phase;
        this._syncVisualState();
        if (!opts.silent) {
            this._persistProgress();
        }
    }

    _syncVisualState () {
        if (this._paletteRestrictor) {
            this._paletteRestrictor.updateAllowed(this._allowedBlocksForCurrentPhase());
        }

        const expectedRole = this._expectedRole();
        if (expectedRole && this._phase !== PHASES.CHAR_SWITCH) {
            this._selectRole(expectedRole);
        }
    }

    _allowedBlocksForCurrentPhase () {
        return (this._config.availableBlocks[this._phase] || []).slice();
    }

    _startIdleScaffolding () {
        this._clearIdleTimers();
        if (this._phase === PHASES.COMPLETE) {
            return;
        }

        const thresholds = this._config.idleScaffolding[this._phase] || [];
        thresholds.forEach((delay, index) => {
            const tier = index + 1;
            this._idleTimers.push(setTimeout(() => {
                if (this._lastInteractionAt + delay > Date.now()) {
                    return;
                }
                if (tier === 4 && !this._idlePausedAt) {
                    this._idlePausedAt = Date.now();
                }
                this._deliverIdleScaffold(tier);
            }, delay));
        });
    }

    _clearIdleTimers () {
        while (this._idleTimers.length > 0) {
            clearTimeout(this._idleTimers.pop());
        }
    }

    _deliverIdleScaffold (tier) {
        this._hintEvents.push({
            phase: this._phase,
            tier,
            character: this._expectedRole(),
            timestamp: new Date().toISOString()
        });

        if (tier === 1 || tier === 3) {
            this._queueNarration(`hint-${this._phase}-${tier}`, this._hintTextForTier(tier), {
                speaker: this._speakerForPhase(this._phase),
                clear: true
            });
        }
    }

    _hintTextForTier (tier) {
        switch (this._phase) {
        case PHASES.TASK_1_BLUUX:
            if (tier === 1) {
                return 'Start with the green flag block, then add a say block for Bluux.';
            }
            return 'Drag the green flag block into Bluux\'s script area, then add a say block.';
        case PHASES.CHAR_SWITCH:
            if (tier === 1) {
                return 'Tap on Dog to give Dog some blocks!';
            }
            return 'See Dog over there? Tap on Dog to switch to Dog\'s script.';
        case PHASES.TASK_2_DOG:
            if (tier === 1) {
                return 'Give Dog a green flag, then a wait block so Dog waits for Bluux, then a say block!';
            }
            return 'Drag the green flag, then the orange wait block, then a say block into Dog\'s script.';
        case PHASES.BONUS:
            if (tier === 1) {
                return 'Try adding wait blocks between say blocks for a real conversation!';
            }
            return 'Use wait blocks to make Bluux and Dog take turns talking.';
        default:
            return '';
        }
    }

    _expectedRole () {
        switch (this._phase) {
        case PHASES.TASK_1_BLUUX:
            return 'bluux';
        case PHASES.CHAR_SWITCH:
        case PHASES.TASK_2_DOG:
            return 'dog';
        case PHASES.BONUS:
        case PHASES.COMPLETE:
            return null; // Free editing — don't force a sprite selection
        default:
            return 'bluux';
        }
    }

    _speakerForPhase (phase) {
        switch (phase) {
        case PHASES.TASK_1_BLUUX:
            return 'bluux';
        case PHASES.CHAR_SWITCH:
            return 'bluux';
        case PHASES.TASK_2_DOG:
            return 'dog';
        default:
            return 'system';
        }
    }

    _buildResumePrompt (phase) {
        switch (phase) {
        case PHASES.TASK_1_BLUUX:
            return 'Welcome back! Help Bluux say hello to Dog. Give Bluux a green flag and a say block.';
        case PHASES.CHAR_SWITCH:
        case PHASES.TASK_2_DOG:
            return 'Welcome back! Bluux is ready. Now tap on Dog and give Dog a green flag and say block too!';
        case PHASES.BONUS:
            return 'Welcome back! Bluux and Dog are talking. Try adding more blocks to make the conversation longer!';
        default:
            return this._config.voiceOver['VO-INTRO'];
        }
    }

    _ensureExpectedSelection () {
        const expected = this._expectedRole();
        if (expected && this._phase !== PHASES.CHAR_SWITCH) {
            this._selectRole(expected);
        }
    }

    _selectRole (role) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || !ScratchJr.stage || !ScratchJr.stage.currentPage) {
            return;
        }
        ScratchJr.stage.currentPage.setCurrentSprite(sprite);
        ScratchJr.stage.currentPage.currentSpriteName = sprite.id;
        ScratchJr.stage.currentPage.lastSprite = sprite.id;
        Thumbs.selectThisSprite(sprite);
        if (this._phase !== PHASES.CHAR_SWITCH) {
            this._selectedRole = role;
        }
    }

    _getSelectedRole () {
        const sprite = ScratchJr.getSprite();
        const spriteName = sprite && sprite.name ? String(sprite.name) : '';
        if (spriteName) {
            const normalizedName = spriteName.toLowerCase().replace(/\s+\d+$/, '');
            const matchedRole = MEMBERS.find((role) => {
                const targetName = String(this._config.members[role].spriteName || '').toLowerCase();
                return normalizedName === targetName || normalizedName.indexOf(targetName) === 0;
            });
            if (matchedRole) {
                return matchedRole;
            }
        }
        return this._selectedRole || null;
    }

    _roleHasAnyBlock (role, blockTypes) {
        return this._getSpriteBlocks(role).some((block) => block && blockTypes.includes(block.blocktype));
    }

    _getSpriteBlocks (role) {
        const sprite = this._getSpriteOwner(role);
        const scriptsDiv = sprite ? gn(sprite.id + '_scripts') : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
            return [];
        }
        return scriptsDiv.owner.getBlocks() || [];
    }

    _getSpriteOwner (role) {
        const targetName = this._config.members[role] && this._config.members[role].spriteName;
        if (!targetName || !ScratchJr.stage || !ScratchJr.stage.currentPage || typeof ScratchJr.stage.currentPage.getSprites !== 'function') {
            return null;
        }
        const spriteIds = ScratchJr.stage.currentPage.getSprites();
        for (let i = 0; i < spriteIds.length; i++) {
            const spriteEl = gn(spriteIds[i]);
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            if (sprite && String(sprite.name).toLowerCase() === String(targetName).toLowerCase()) {
                return sprite;
            }
        }
        return null;
    }

    _resetForReplay () {
        this._flagTapCount = 0;
        this._characterSwitches = 0;
        this._milestones = {
            bluuxFlag: false,
            bluuxSay: false,
            dogFlag: false,
            dogSay: false
        };
        this._hintEvents = [];
        this._voiceHistory = [];
        this._sessionStartedAt = Date.now();
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        ScratchJr.stopStrips();
        ScratchJr.resetSprites();
        this._setPhase(this._config.restartPhase || PHASES.TASK_1_BLUUX, {silent: true});
        this._selectRole('bluux');
        this._syncVisualState();
        this._startIdleScaffolding();
        this._queueNarration('VO-INTRO', null, {speaker: 'bluux', clear: true});
        this._persistProgress({
            status: 'in_progress',
            completedAt: null
        });
    }

    // ──────────────────────── Narration ────────────────────────

    _queueNarration (id, fallbackText, opts = {}) {
        const text = fallbackText || this._config.voiceOver[id];
        if (!text) {
            return Promise.resolve();
        }

        if (opts.clear) {
            this._stopNarration();
        }

        const runId = this._speechRunId;
        this._lastNarrationId = id;
        this._lastNarrationText = text;
        const entry = {
            id,
            text,
            phase: this._phase,
            speaker: opts.speaker || 'system',
            spokenAt: new Date().toISOString()
        };
        this._voiceHistory.push(entry);
        this._voiceHistory = this._voiceHistory.slice(-30);

        this._speechQueue = this._speechQueue.then(async () => {
            if (this._destroyed || runId !== this._speechRunId) {
                return;
            }
            await this._speakUtterance(text, opts.speaker || 'system');
        });

        return this._speechQueue;
    }

    _stopNarration () {
        this._speechRunId++;
        this._speechQueue = Promise.resolve();
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            try {
                window.speechSynthesis.cancel();
            } catch (_) {}
        }
    }

    _speakUtterance (text, speaker) {
        const preferredVoice = this._getPreferredVoice();
        const settings = this._speakerSettings(speaker);
        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            return this._wait(Math.min(7200, Math.max(1800, text.split(/\s+/).length * 360)));
        }

        return new Promise((resolve) => {
            let resolved = false;
            let timeoutId = null;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                resolve();
            };

            try {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
                if (preferredVoice) {
                    utterance.voice = preferredVoice;
                }
                utterance.rate = settings.rate;
                utterance.pitch = settings.pitch;
                utterance.volume = 1;
                utterance.onend = finish;
                utterance.onerror = finish;
                timeoutId = setTimeout(finish, Math.min(9000, Math.max(2200, text.split(/\s+/).length * 420)));
                window.speechSynthesis.speak(utterance);
            } catch (_) {
                finish();
            }
        });
    }

    _speakerSettings (speaker) {
        switch (speaker) {
        case 'bluux':
            return {rate: 0.94, pitch: 1.14};
        case 'dog':
            return {rate: 0.9, pitch: 0.96};
        default:
            return {rate: 0.92, pitch: 1.08};
        }
    }

    _initVoiceSelection () {
        if (!window.speechSynthesis) {
            return;
        }
        this._refreshPreferredVoice();
        if (!this._speechVoice && typeof window.speechSynthesis.addEventListener === 'function') {
            window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _waitForPreferredVoice (timeoutMs = 2600) {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return Promise.resolve();
        }
        this._refreshPreferredVoice();
        if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            let resolved = false;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                resolve();
            };
            const poll = setInterval(() => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    clearInterval(poll);
                    clearTimeout(timeout);
                    finish();
                }
            }, 100);
            const timeout = setTimeout(() => {
                clearInterval(poll);
                finish();
            }, timeoutMs);
        });
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
        this._speechVoiceScore = selection ? selection.score : 0;
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }
        const voices = window.speechSynthesis.getVoices();
        if (!Array.isArray(voices) || voices.length < 1) {
            return null;
        }
        const ranked = voices
            .map((voice) => ({voice, score: this._scoreVoice(voice)}))
            .sort((a, b) => b.score - a.score);
        return ranked[0] && ranked[0].score > 0 ? ranked[0] : null;
    }

    _scoreVoice (voice) {
        return scorePreferredNarrationVoice(voice, window, {profile: 'helloFriend'});
    }

    _wait (ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async _persistProgress (overrides = {}) {
        const now = Date.now();
        const elapsedMs = Math.max(0, now - this._sessionStartedAt - this._idleTimeMs);
        const completedPhase = this._phase === PHASES.COMPLETE || this._phase === PHASES.BONUS;
        const status = overrides.status || (completedPhase ? 'completed' : 'in_progress');
        const completedAt = Object.prototype.hasOwnProperty.call(overrides, 'completedAt')
            ? overrides.completedAt
            : (status === 'completed' ? new Date().toISOString() : undefined);
        await ActivityStorageService.saveProgress(this._activityData.activityId, {
            status,
            attempts: overrides.attempts !== undefined ? overrides.attempts : this._flagTapCount,
            currentState: this._phase,
            elapsedMs,
            milestones: {...this._milestones},
            hintEvents: this._hintEvents.slice(-50),
            flagTapCount: this._flagTapCount,
            characterSwitches: this._characterSwitches,
            completedAt
        });
    }
}
